/* ──────────────────────────────────────────────────────────────
 *  Compute API — gen(), sampling, prediction endpoints
 * ────────────────────────────────────────────────────────────── */

import type { AxiosProgressEvent } from 'axios';
import axiosClient from './axiosClient';
import type { BushingType } from '@/types/bushing';

/** Payload sent to the `/compute/predict` endpoint. */
export interface ComputeGenPayload {
  sleeve_length: number;
  sleeve_thk: number;
  core_length: number;
  control_arm_id: number;
  core_od_min: number;
  core_od_max: number;
  collar_od?: number;
  h?: number;
  linear_travel?: number;
  Cx_target?: number | null;
  Cy_target?: number | null;
  Cz_target?: number | null;
  Torsion_target?: number | null;
  Conical_target?: number | null;
  shore_a: number;
  stiffness_priorities?: Record<string, number> | null;
}

/** Response from the compute endpoint. */
export interface ComputeGenResponse {
  bushing_type: string;
  total_samples: number;
  samples: Record<string, unknown>[];
  best_sample: Record<string, unknown> | null;
  best_sample_id: string | null;
  failed_constraints: string[];
}

/** Trigger a compute run (predict + best-sample selection). */
export async function computeGen(
  bushingType: BushingType,
  payload: ComputeGenPayload,
  onProgress?: (pct: number, message?: string) => void,
): Promise<ComputeGenResponse> {
  let uploadDone = false;

  const { data } = await axiosClient.post<ComputeGenResponse>(
    `/compute/predict/${encodeURIComponent(bushingType)}`,
    payload,
    {
      onUploadProgress: (e: AxiosProgressEvent) => {
        if (!onProgress) return;
        if (e.total) {
          // Upload maps to 5–15% range
          const pct = 5 + Math.round((e.loaded / e.total) * 10);
          onProgress(pct, 'Sending request to model…');
        }
        if (e.loaded === e.total && !uploadDone) {
          uploadDone = true;
          onProgress(15, 'Generating predictions…');
        }
      },
      onDownloadProgress: (e: AxiosProgressEvent) => {
        if (!onProgress) return;
        if (e.total) {
          // Download maps to 30–45% range
          const pct = 30 + Math.round((e.loaded / e.total) * 15);
          onProgress(pct, 'Receiving samples…');
        } else {
          // No content-length header — signal response started
          onProgress(35, 'Receiving samples…');
        }
      },
    },
  );
  return data;
}
